import { Link } from "react-router";
import type { MessageKey } from "@wato/i18n";
import type { OverviewChannel, OverviewChannelStatus } from "../../api/overview-types";
import { ChartCard } from "../../components/analytics/cards";
import { looksTechnical } from "../../lib/technical-text";
import { useOverviewFormat } from "./useOverviewFormat";

const TONE: Readonly<Record<OverviewChannelStatus, string>> = {
  connected: "bg-success-bg text-success-fg",
  degraded: "bg-warning-bg text-warning-fg",
  disconnected: "bg-danger-bg text-danger-bg-fg",
};

/** The channel kind in plain words (same wording as the channel pickers). */
function kindKey(type: string): MessageKey {
  return (
    type === "whatsapp_waha"
      ? "dashboard.aiAgents.channel.qr"
      : type === "whatsapp_cloud"
        ? "dashboard.aiAgents.channel.cloud"
        : type.startsWith("instagram")
          ? "dashboard.aiAgents.channel.instagram"
          : "dashboard.aiAgents.channel.other"
  ) as MessageKey;
}

/** OWN-164 channel health: one chip per channel (status in words and colour) and a link to Channels. */
export function ChannelChips({
  workspaceId,
  channels,
}: {
  readonly workspaceId: string;
  readonly channels: readonly OverviewChannel[];
}) {
  const { t } = useOverviewFormat();
  return (
    <ChartCard title={t("dashboard.overview.section.channels")}>
      <ul data-testid="overview-channels" className="m-0 flex list-none flex-wrap gap-2 p-0">
        {channels.map((c) => {
          const kind = t(kindKey(c.type));
          const name = c.name && !looksTechnical(c.name) ? c.name : "";
          return (
            <li
              key={c.id}
              data-testid={`overview-channel-${c.status}`}
              className={`flex min-w-0 max-w-full items-center gap-2 rounded-full px-3 py-1 text-sm ${TONE[c.status]}`}
            >
              <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-current" />
              <span className="min-w-0 break-words font-medium">{name || kind}</span>
              {name ? <span className="text-xs">{kind}</span> : null}
              <span>· {t(`dashboard.overview.channel.${c.status}`)}</span>
            </li>
          );
        })}
      </ul>
      <Link
        to={`/w/${workspaceId}/channels`}
        className="self-start text-sm text-on-action-ghost hover:underline"
      >
        {t("dashboard.overview.channel.manage")}
      </Link>
    </ChartCard>
  );
}
